import { useQuery } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { phoneCallOutcomesQueryOptions } from '../../../../queries/PhoneCall/usePhoneCallOutcomeColorsQuery';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { Label } from '../../../../web/components-v2/Label';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { FieldError } from '../../../../web/components-v2/Field';
import { FieldKey } from '../../../viewModels/form';
import { buildOutcomeOptions, toggleOutcome } from '../../leadView/components/OutcomeSelect/utils';
import { styleSheet } from '../styles';
import { IOptionItem } from '../types';

interface IProps {
	defaultValues: string[];
	onChange: (item: IOptionItem<FieldKey>[]) => void;
}

export const OutcomeChecklist: React.FC<IProps> = ({ defaultValues, onChange }) => {
	const userSession = useUserSession();
	const outcomesQuery = useQuery({
		...phoneCallOutcomesQueryOptions(userSession),
		meta: {
			errorLogging: { category: 'OutcomeChecklist', action: 'phoneCallOutcomesQuery' },
			// The web service rejects with an operation result, not an Error.
			toastErrorMessage: (error: Error) =>
				(error as unknown as { systemMessage?: string }).systemMessage ?? 'Unable to load outcomes',
		},
	});
	const [edited, setEdited] = useState<IOptionItem<FieldKey>[] | null>(null);

	if (outcomesQuery.isPending) {
		return <LoadingSpinner type='small' />;
	}
	if (outcomesQuery.isError) {
		return <FieldError>Unable to load outcomes.</FieldError>;
	}

	const outcomeOptions = edited ?? buildOutcomeOptions(outcomesQuery.data, defaultValues);

	const handleChange = (id: string, checked: boolean) => {
		const outcomes = toggleOutcome(outcomeOptions, id, checked);
		setEdited(outcomes);
		onChange(outcomes);
	};

	return (
		<div className={css(styleSheet.outcomeList)}>
			{outcomeOptions.map((parent: IOptionItem<FieldKey>) => (
				<div key={parent.id} className={css(styleSheet.outcomeGroup)}>
					<Label styles={[styleSheet.outcomeParent]} htmlFor={`outcome-${parent.id}`}>
						<Checkbox
							id={`outcome-${parent.id}`}
							checked={!!parent.checked}
							onCheckedChange={value => handleChange(parent.id, value === true)}
						/>
						{parent.text}
					</Label>
					{parent?.children?.length ? (
						<div className={css(styleSheet.outcomeChildren)}>
							{parent.children.map((child: IOptionItem<FieldKey>) => (
								<Label key={child.id} htmlFor={`outcome-${child.id}`}>
									<Checkbox
										id={`outcome-${child.id}`}
										checked={!!(parent.checked || child.checked)}
										onCheckedChange={value => handleChange(child.id, value === true)}
									/>
									{child.label}
								</Label>
							))}
						</div>
					) : null}
				</div>
			))}
		</div>
	);
};
